import { Button } from 'copilot-design-system';
import moment from 'moment';
import React, { useContext } from 'react';

import { RouteContext, useAssistantContext } from 'src/context';
import { MessageDraft } from 'src/features/assistant/action-item/MessageDraft';
import { ShareContractDraft } from 'src/features/assistant/action-item/ShareContractDraft';
import { ShareFormDraft } from 'src/features/assistant/action-item/ShareFormDraft';
import { TaskDraft } from 'src/features/assistant/action-item/TaskDraft';
import history from 'src/history';
import { useAppDispatch } from 'src/hooks/useStore';
import { BillingWidget } from 'src/routes/assistant/BillingWidget';
import { toggleHelpCenterModalAction } from 'src/store/ui/actions';
import { CTA } from 'src/types/stream-chat';

interface ActionItemProps {
  ctas: CTA[];
}
export const validateStringType = (value: unknown): value is string =>
  typeof value === 'string';

export const ActionItem: React.FC<ActionItemProps> = ({ ctas }) => {
  const { setMessageDraft, incrementExtensionTabKey } = useAssistantContext();
  const dispatch = useAppDispatch();
  const { query, pathname } = useContext(RouteContext);

  const pathParts = pathname.split('/');
  const basePath = pathParts.slice(0, -1).join('/');
  const endPath = pathParts[pathParts.length - 1];

  const handleCTAClick = (cta: CTA) => {
    // parse the type in the cta to find the appropriate app
    // and navigate to the app with the prefilled data. The prefilled
    // data is provided as a json object which should be passed
    // as a query parameter as a "pf" query param.
    let path = '';
    const params = new URLSearchParams();

    switch (cta.type) {
      case 'action.contact_support': {
        path = pathname;
        if (query.channelId) {
          params.set('channelId', query.channelId);
        }
        dispatch(toggleHelpCenterModalAction(true));
        params.set('action', 'contactSupport');
        break;
      }
      case 'action.create_invoice': {
        path = '/invoices/edit';
        break;
      }
      case 'action.create_subscription': {
        path = '/subscriptions/edit';
        break;
      }
      case 'action.send_message': {
        if (endPath === 'messages') {
          // Handle message draft - don't navigate, just set the draft
          if (
            cta.message_data?.body &&
            typeof cta.message_data.body === 'string'
          ) {
            setMessageDraft(cta.message_data.body);
          }
          return;
        } else {
          path = `${basePath}/messages`;
          params.set('action', 'sendMessage');
          break;
        }
      }
      default: {
        if (cta.type.startsWith('action.custom_app.')) {
          const [, , appAction] = cta.type.split('.');
          const { appId } = cta;
          path = `${basePath}/${appId}`;
          params.set('action', appAction ?? '');
        }
        break;
      }
    }

    // Use the new structured data fields
    let dataToEncode: Record<string, unknown> | null = null;

    switch (cta.type) {
      case 'action.create_invoice':
        dataToEncode = cta.invoice_data ? { ...cta.invoice_data } : null;
        break;
      case 'action.send_message':
        dataToEncode = cta.message_data ? { ...cta.message_data } : null;
        break;
      case 'action.custom_app.create_task':
      case 'action.create_task':
        if (cta.task_data && cta.task_data.dueDate) {
          // currently dates created by the assistant are at midnight in UTC.
          // we want the dueDate to be an ISO string that is midnight in the
          // current locale so that the tasks app can handle it correctly
          cta.task_data.dueDate = moment(
            cta.task_data.dueDate.split('T')[0],
          ).toISOString();
        }
        dataToEncode = cta.task_data ? { ...cta.task_data } : null;
        break;
      case 'action.create_subscription':
        dataToEncode = cta.subscription_data
          ? { ...cta.subscription_data }
          : null;
        break;
      case 'action.send_contract':
        dataToEncode = cta.contract_data ? { ...cta.contract_data } : null;
        break;
      case 'action.send_form':
        dataToEncode = cta.form_data ? { ...cta.form_data } : null;
        break;
      case 'action.contact_support':
        dataToEncode = cta.support_data ? { ...cta.support_data } : null;
        break;
      default:
        // For custom apps, try to find relevant data
        if (cta.type.startsWith('action.custom_app.')) {
          dataToEncode =
            (cta.task_data ? { ...cta.task_data } : null) ||
            (cta.message_data ? { ...cta.message_data } : null) ||
            (cta.invoice_data ? { ...cta.invoice_data } : null);
        }
        break;
    }

    if (dataToEncode) {
      const encodedData = encodeURIComponent(JSON.stringify(dataToEncode));
      params.set('pf', encodedData);
    }

    if (path) {
      const nextPath = `${path}?${params.toString()}`;
      if (
        cta.type === 'action.create_invoice' ||
        cta.type === 'action.create_subscription'
      ) {
        history.push(nextPath);
      } else {
        history.replace(nextPath);
      }

      // Check if we need to force a page reload for create_task on the same app page
      const isCreateTaskAction =
        cta.type === 'action.custom_app.create_task' &&
        params.get('action') === 'create_task';
      const isCurrentAppPage = cta.appId && pathname.includes(`/${cta.appId}`);

      if (isCreateTaskAction && isCurrentAppPage) {
        // force the extension tab to rerender so it can consume the new query params
        // the delay is to ensure that this happens after the query params are updated
        setTimeout(() => incrementExtensionTabKey(), 100);
      }
    }
  };

  if (!ctas || ctas.length === 0) {
    return null;
  }

  return (
    <div>
      {ctas.map((cta, index) => (
        <div key={`cta-${index}`}>
          {cta.type === 'action.send_message' ? (
            <MessageDraft cta={cta} handleCTAClick={handleCTAClick} />
          ) : cta.type === 'action.custom_app.create_task' ? (
            <TaskDraft cta={cta} handleCTAClick={handleCTAClick} />
          ) : cta.type === 'action.create_invoice' ? (
            <BillingWidget
              cta={cta}
              onCTAClick={handleCTAClick}
              widgetName="INVOICE DRAFT"
              buttonName="Create invoice"
            />
          ) : cta.type === 'action.create_subscription' ? (
            <div>
              <BillingWidget
                cta={cta}
                onCTAClick={handleCTAClick}
                widgetName="SUBSCRIPTION DRAFT"
                isSubscription
                buttonName="Create subscription"
              />
            </div>
          ) : cta.type === 'action.send_contract' ? (
            <ShareContractDraft cta={cta} onCTAClick={handleCTAClick} />
          ) : cta.type === 'action.send_form' ? (
            <ShareFormDraft cta={cta} onCTAClick={handleCTAClick} />
          ) : cta.type === 'action.contact_support' ? (
            <Button
              label="Message support"
              variant="secondary"
              onClick={() => handleCTAClick(cta)}
              prefixIcon="Send"
              className="[&>svg]:box-content text-black-heading"
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};
